import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useNullableSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  supplierRecordQuery,
  type SupplierRecord,
} from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { resolveSettingsSupplierId } from "@/app/Suppliers/Utils/globalSupplier";

/**
 * Supplier a Settings screen is editing. Mirrors {@link useGlobalSupplier} but
 * falls back to the settings-scoped `?supplier=` search param when no global
 * supplier is selected — so an admin on the "all suppliers" view can edit a
 * single supplier's settings without changing the global selection.
 */
export function useSettingsSupplier() {
  const navigate = useNavigate();
  const { supplierId: param } = useParams({ strict: false }) as {
    supplierId?: string;
  };
  const { supplier: searchSupplier } = useSearch({ strict: false }) as {
    supplier?: string;
  };

  const isGlobalSupplier = !!param;
  const supplierId = resolveSettingsSupplierId(
    { supplierId: param },
    { supplier: searchSupplier },
  );

  const { data: record } = useNullableSuspenseGqlQuery(
    supplierRecordQuery,
    { id: supplierId ?? "" },
    !!supplierId,
  );

  function setSettingsSupplier(nextSupplierId: string | null) {
    return navigate({
      to: ".",
      params: (prev) => prev,
      search: (prev: { supplier?: string }) => ({
        ...prev,
        supplier: nextSupplierId ?? undefined,
      }),
    });
  }

  return {
    supplierId,
    hasSupplier: !!supplierId,
    isGlobalSupplier,
    supplier: (record?.supplier ?? undefined) as SupplierRecord,
    setSettingsSupplier,
  };
}
